import { defineStore } from 'pinia';
import axios from 'axios';
import { ref } from 'vue';
import { assetURL } from '../../utils/url';
import { phone, email, landline, required } from '../../utils/validator';

export const useVisitStore = defineStore('inquiry', () => {
    const currentStep = ref(1);
    const totalSteps = ref(3);
    const estateList = ref([]);
    const estateListResidences = ref([]);
    const estateListOffices = ref([]);
    const officeTypes = ref([]);
    const residenceTypes = ref([]);
    const propertyList = ref([]);
    const selectedType = ref('Residences');
    const selectedEstate = ref('');
    const selectedProperty = ref('');
    const loading = ref(false);
    const hasPropertySelectionError = ref(false);
    const propertySelectionError = ref({
        estate: '',
        property: '',
    });
    const selectedSchedule = ref(null);
    const scheduleList = ref([
        {
            id: 0,
            image: `${assetURL}images/placeholder/schedule-1.png`,
        },
        {
            id: 1,
            image: `${assetURL}images/placeholder/schedule-2.png`,
        },
    ]);

    function validatePropertySelection() {
        if (selectedEstate.value === '') {
            hasPropertySelectionError.value = true;
            propertySelectionError.value.estate = 'This field is required';
        }
        if (selectedProperty.value === '') {
            hasPropertySelectionError.value = true;
            propertySelectionError.value.property = 'This field is required';
        }
    }
    function resetPropertySelectionError() {
        hasPropertySelectionError.value = false;
        propertySelectionError.value = {
            estate: '',
            property: '',
        };
    }

    const personalDetail = ref({
        firstname: '',
        middlename: '',
        lastname: '',
        email: '',
        phone: '',
        landline: '',
        message: '',
    });
    const hasDetailError = ref(false);
    const personalDetailError = ref({
        firstname: '',
        middlename: '',
        lastname: '',
        email: '',
        phone: '',
        landline: '',
        message: '',
    });

    function nextStep() {
        if (currentStep.value >= totalSteps.value) {
            return;
        }
        if (currentStep.value === 1) {
            if (selectedSchedule.value === null) {
                return;
            }
        }
        if (currentStep.value === 2) {
            resetPropertySelectionError();
            validatePropertySelection();
            if (hasPropertySelectionError.value) {
                return;
            }
        }
        currentStep.value++;
    }

    function prevStep() {
        if (currentStep.value == 1) {
            return;
        }
        currentStep.value--;
    }

    function setEstateList(callback) {
        axios
            .get('/contact-us/getPropertyListVisit/')
            .then((response) => {
                estateList.value = response.data.estates;
                officeTypes.value = response.data.offices;
                residenceTypes.value = response.data.residence;
                estateListResidences.value = response.data.estatesResidences;
                estateListOffices.value = response.data.estatesOffices;
                callback(response.data);
                preSelectProperty();
            })
            .catch((error) => {
                console.error(`Error fetching data: ${error}`);
            });
    }

    function preSelectProperty() {
        const urlParams = new URLSearchParams(window.location.search);
        const type = urlParams.get('type');
        const estate = urlParams.get('estate');
        const property = urlParams.get('property');
        if (type && estate && property) {
            setSelectedType(type);
            setSelectedEstate(estate);
            setSelectedProperty(property);
        }
    }

    function setSchedule(schedule) {
        selectedSchedule.value = schedule;
    }

    function setPropertyList() {
        propertySelectionError.value.property = '';
        if (selectedType.value == 'Offices') {
            propertyList.value = officeTypes.value.filter(
                (property) => property.estate_id == selectedEstate.value
            );
        } else {
            propertyList.value = residenceTypes.value.filter(
                (property) => property.estate_id == selectedEstate.value
            );
        }
    }

    function setSelectedType(type) {
        propertySelectionError.value.property = '';
        propertySelectionError.value.estate = '';
        selectedEstate.value = '';
        selectedProperty.value = '';
        selectedType.value = type;
        if(type == 'Offices') {
            estateList.value = estateListOffices.value;
        }  else {
            estateList.value = estateListResidences.value;
        }
    }

    function setSelectedEstate(id) {
        propertySelectionError.value.estate = '';
        selectedProperty.value = '';
        selectedEstate.value = id;
        setPropertyList();
    }

    function setSelectedProperty(id) {
        setSelectedEstate(getEstateIDBasedOnPropertyID(id));
        selectedProperty.value = id;
    }

    function getEstateByUrl(url) {
        axios.get(`/contact-us/get-estate-by-url/${url}`).then((response) => {
            selectedEstate.value = response.data.id;
            setPropertyList();
        });
    }

    function getEstateIDBasedOnPropertyID(property_id) {
        if (selectedType.value === 'Offices') {
            console.log('officeTypes.value', officeTypes.value);
            console.log('property_id', property_id);

            const office = officeTypes.value.filter(
                (property) => property.id == property_id
            );

            return office[0]?.estate_id;
        }
        return residenceTypes.value.filter(
            (property) => property.id == property_id
        )[0].estate_id;
    }

    function getPropertyByUrl(url) {
        axios.get(`/contact-us/get-property-by-url/${url}`).then((response) => {
            setSelectedEstate(getEstateIDBasedOnPropertyID(response.data.id));
            selectedProperty.value = response.data.id;
        });
    }

    function setDetailValue(key, value) {
        personalDetail.value[key] = value;
    }

    function resetDetailErrorMessage() {
        hasDetailError.value = false;
        personalDetailError.value = {
            firstname: '',
            middlename: '',
            lastname: '',
            email: '',
            phone: '',
            landline: '',
            message: '',
        };
    }

    function submit() {
        if (loading.value == true) {
            return;
        }
        loading.value = true;
        validate();
        if (hasDetailError.value) {
            loading.value = false;
            return;
        }
        const details = { ...personalDetail.value };
        axios
            .post('/contact-us/visit/new', {
                ...personalDetail.value,
                property: selectedProperty.value,
                schedule: selectedSchedule.value,
            })
            .then((response) => {
                if (response.data.status) {
                    loading.value = false;
                    return location.replace(
                        `/contact-us/thankyou?name=${details.firstname}&property=${selectedProperty.value}`
                    );
                }
            })
            .catch((error) => {
                console.log(error);
                loading.value = false;
                if (error.response.status === 422) {
                    for (const [key, value] of Object.entries(
                        error.response.data.errors
                    )) {
                        if (
                            typeof personalDetailError.value[key] !==
                            'undefined'
                        ) {
                            personalDetailError.value[key] = value[0];
                        }
                    }
                }
            });
    }

    function validate() {
        resetDetailErrorMessage();
        const details = { ...personalDetail.value };
        for (let [key, value] of Object.entries(details)) {
            value = value.trim();
            if (key == 'phone' && phone(value) === false) {
                personalDetailError.value[key] = 'Invalid phone number';
                hasDetailError.value = true;
            }

            if (key == 'email' && email(value) === false) {
                personalDetailError.value[key] = 'Invalid email address';
                hasDetailError.value = true;
            }

            if (
                key == 'landline' &&
                required(value) == true &&
                landline(value) === false
            ) {
                personalDetailError.value[key] = 'Invalid landline number';
                hasDetailError.value = true;
            }

            if (['firstname', 'lastname', 'phone', 'email'].includes(key)) {
                if (value == '') {
                    personalDetailError.value[key] = 'This field is required';
                    hasDetailError.value = true;
                }
            }
        }
    }

    return {
        currentStep,
        totalSteps,
        nextStep,
        prevStep,

        estateList,
        setEstateList,
        officeTypes,
        residenceTypes,
        propertyList,
        selectedType,
        setSelectedType,
        selectedEstate,
        setSelectedEstate,
        selectedProperty,
        setSelectedProperty,
        hasPropertySelectionError,
        propertySelectionError,

        getEstateByUrl,
        getPropertyByUrl,

        personalDetail,
        setDetailValue,
        personalDetailError,
        submit,
        hasDetailError,

        setSchedule,
        scheduleList,
        selectedSchedule,
    };
});
